import { BaseApi, ApiResponse } from "../utils/request";

export interface FirebaseBindResponse {
  status: number;
  time?: number;
  maxAge?: number;
  user: any;
  firebaseRegister?: boolean;
  extra?: {
    pixelEvent?: {
      eventId: string;
      fbEventName: string;
      ttEventName: string;
    };
  };
}

export interface FirebaseCheckRegisteredResponse {
  status: number;
  extra?: {
    registered: boolean;
    email: string;
  };
}

/**
 * Auth Config 接口返回数据
 */
export interface AuthConfig {
  enableWafCode: boolean;
  wafSiteKey: string;
  formKey: string;
  disablePhone: boolean;
}

/**
 * 重置密码请求参数
 */
export interface ResetPasswordRequest {
  password: string;
  // 方式1：手机号验证码
  phone?: string;
  code?: string;
  countryId?: string;
  // 方式2：邮箱验证码
  email?: string;
  // 方式3：邮箱 token
  token?: string;
}

/**
 * Auth API 类
 * 继承 BaseApi，提供认证相关的 API 接口
 */
export class AuthApi extends BaseApi {
  constructor() {
    super();
  }

  /**
   * 获取认证配置
   * @returns Promise<ApiResponse<AuthConfig, true>>
   */
  async getAuthConfig(): Promise<AuthConfig | null> {
    const response = await this.post<ApiResponse<AuthConfig, true>>(
      "/api/auth/auth_config",
      {}
    );
    return response.data.model || null;
  }

  /**
   * 发送忘记密码邮箱验证码
   * @param email 邮箱地址
   */
  async sendForgetEmail(email: string): Promise<void> {
    await this.post<ApiResponse<any>>("/api/auth/send_forget_email", {
      email,
    });
  }

  /**
   * 发送忘记密码手机验证码
   * @param phone 手机号（含国家码，与 reset_password 提交一致）
   */
  async sendForgetPhoneCode(phone: string): Promise<void> {
    await this.post<ApiResponse<any>>("/api/auth/send_forget_phone_code", {
      phone,
    });
  }

  /**
   * 修改密码（登录后修改）
   * @param params oldPassword, newPassword, newPasswordConfirm 均为必填
   */
  async changePassword(params: {
    oldPassword: string;
    newPassword: string;
    newPasswordConfirm: string;
  }): Promise<void> {
    await this.post<ApiResponse<any>>("/api/auth/change_password", params);
  }

  /**
   * 重置密码
   * @param params - 重置密码参数
   * @returns Promise<void>
   */
  async resetPassword(params: ResetPasswordRequest): Promise<void> {
    await this.post<ApiResponse<null, true>>(
      "/api/auth/reset_password",
      params
    );
  }
  /**
   * 三方登录绑定（Firebase Google/Facebook）
   * @param params idToken, type, confId, countryId?, inviteCode?, shareTag?
   * @returns Promise 成功时返回 axios response，status===0 表示成功
   */
  async firebaseCheckRegistered(params: {
    idToken: string;
    type: string;
    confId: string;
  }): Promise<FirebaseCheckRegisteredResponse> {
    const response = await this.post<FirebaseCheckRegisteredResponse>(
      "/api/auth/firebase_check_registered",
      params
    );
    return response.data;
  }

  async firebaseBind(params: {
    idToken: string;
    type: string;
    confId: string;
    countryId?: string;
    inviteCode?: string;
    shareTag?: string;
    phone?: string;
    password?: string;
    confirmPassword?: string;
  }): Promise<FirebaseBindResponse> {
    const response = await this.post<FirebaseBindResponse>(
      "/api/auth/firebase_bind",
      params
    );
    return response.data;
  }
}

export const authApi = new AuthApi();
export const getAuthConfig = () => authApi.getAuthConfig();
export const sendForgetEmail = (email: string) =>
  authApi.sendForgetEmail(email);
export const sendForgetPhoneCode = (phone: string) =>
  authApi.sendForgetPhoneCode(phone);
export const changePassword = (params: {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}) => authApi.changePassword(params);
export const resetPassword = (params: ResetPasswordRequest) =>
  authApi.resetPassword(params);

export const firebaseCheckRegistered = (params: {
  idToken: string;
  type: string;
  confId: string;
}): Promise<FirebaseCheckRegisteredResponse> =>
  authApi.firebaseCheckRegistered(params);

export const firebaseBind = (params: {
  idToken: string;
  type: string;
  confId: string;
  countryId?: string;
  inviteCode?: string;
  shareTag?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
}): Promise<FirebaseBindResponse> => authApi.firebaseBind(params);
